import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from '@ant-design/icons';
import {
  Card,
  Typography,
  Input,
  Flex,
  Checkbox,
  Row,
  Col,
  ConfigProvider,
} from 'antd';
import { ReactComponent as Search } from '../../../assets/icons/search.svg';
import { ReactComponent as SearchBlack } from '../../../assets/icons/search-black.svg';
import { useTheme } from '../../../context/ThemeContext';
import { DARK } from '../../../utilities/constants';
import EmptyCard from '../../../components/EmptyCard/EmptyCard';
import { sanitizeInput } from '../../../utilities/commonFunctions';

const { Text, Title } = Typography;
const OngoingTripsList = ({
  tripData,
  setTripData,
  selectedTripsCount,
  setSelectedTripsCount,
  loading,
}) => {
  const { theme } = useTheme();
  const [filteredData, setFilteredData] = useState(tripData);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setFilteredData(
      tripData.filter(
        item =>
          item?.trip_id?.includes(searchText) ||
          item?.vehicle_number?.includes(searchText)
      )
    );
    setSelectedTripsCount(
      tripData.reduce((acc, curr) => (curr?.checked ? acc + 1 : acc), 0)
    );
  }, [searchText, tripData]);

  const onSearchTextChange = e => {
    setSearchText(sanitizeInput(e.target.value));
  };

  const handleCheckChange = (e, item) => {
    if (e.target.checked) {
      setSelectedTripsCount(selectedTripsCount + 1);
    } else {
      setSelectedTripsCount(selectedTripsCount - 1);
    }
    const updatedTripData = tripData.map(trip => {
      if (trip?.trip_id === item?.trip_id) {
        return {
          ...trip,
          checked: e.target.checked,
        };
      }
      return trip;
    });
    const updatedFilteredData = filteredData.map(trip => {
      if (trip?.trip_id === item?.trip_id) {
        return {
          ...trip,
          checked: e.target.checked,
        };
      }
      return trip;
    });
    setTripData(updatedTripData);
    setFilteredData(updatedFilteredData);
  };

  const handleCheckAllChange = e => {
    if (e.target.checked) {
      setSelectedTripsCount(tripData?.length);
    } else {
      setSelectedTripsCount(0);
    }
    const updatedTripData = tripData.map(trip => {
      return {
        ...trip,
        checked: e.target.checked,
      };
    });
    const updatedFilteredData = filteredData.map(trip => {
      return {
        ...trip,
        checked: e.target.checked,
      };
    });
    setTripData(updatedTripData);
    setFilteredData(updatedFilteredData);
  };
  return (
    <ConfigProvider
      theme={{
        token: {
          colorBgContainer: '#71767C',
          borderRadiusSM: 0,
          colorBorder: '#71767C',
        },
        components: {
          Typography: {
            titleMarginTop: '4px',
            titleMarginBottom: '16px',
          },
        },
      }}
    >
      <Card size="small" variant="borderless" className="full-height">
        <Title level={4} style={{ marginTop: '4px' }}>
          Ongoing Trips
        </Title>
        <Flex gap={16} vertical>
          <Input
            onChange={onSearchTextChange}
            size="large"
            placeholder="Trip ID/ Vehicle No."
            suffix={
              <Icon
                component={theme === DARK ? Search : SearchBlack}
                width={20}
                height={20}
              />
            }
            className="input-search"
            disabled={loading}
          />
          <Flex justify="space-between">
            <Flex gap={6}>
              <Checkbox
                checked={selectedTripsCount === tripData?.length}
                indeterminate={
                  selectedTripsCount !== 0 &&
                  selectedTripsCount !== tripData?.length
                }
                onChange={handleCheckAllChange}
                disabled={loading}
              />
              <Text>All</Text>
            </Flex>
            <Text strong>{`${selectedTripsCount}/${tripData?.length}`}</Text>
          </Flex>
        </Flex>
        {filteredData?.length === 0 ? (
          <EmptyCard description={<Text>No Trips Found</Text>} />
        ) : (
          <Flex gap={0} vertical className="list-scroll">
            {filteredData.map(item => (
              <Row key={item?.key} gutter={6}>
                <Col flex="18px" className="tripcard-col">
                  <Checkbox
                    checked={item?.checked}
                    onChange={e => handleCheckChange(e, item)}
                  />
                </Col>
                <Col flex="auto">
                  <Card
                    className="tripitem-card"
                    variant="borderless"
                    style={{
                      borderLeftColor: theme === DARK ? '#3573E0' : '#1DAA00',
                      backgroundColor: item?.checked
                        ? 'var(--selected-bg-color, #D0D4D8)'
                        : 'var(--secondary-fill-color, #2E3033)',
                    }}
                  >
                    <Flex vertical>
                      <Typography.Text
                        className="tripitem-vendorname"
                        style={{
                          color:
                            item?.checked &&
                            'var(--selected-text-color, #000000)',
                        }}
                      >
                        {`${item?.vendor}`}
                      </Typography.Text>
                      <Typography.Text
                        className="tripitem-trip-vehicle"
                        style={{
                          color:
                            item?.checked &&
                            'var(--selected-text-color, #000000)',
                        }}
                      >
                        {`₹${item?.trip_id} | ${item?.vehicle_number}`}
                      </Typography.Text>
                    </Flex>
                  </Card>
                </Col>
              </Row>
            ))}
          </Flex>
        )}
      </Card>
    </ConfigProvider>
  );
};

OngoingTripsList.propTypes = {
  tripData: PropTypes.array.isRequired,
  setTripData: PropTypes.func.isRequired,
  selectedTripsCount: PropTypes.number.isRequired,
  setSelectedTripsCount: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default OngoingTripsList;
